import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowUpCircle, ArrowDownCircle, RotateCcw, Download, History, Search } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface TraceRow {
  id: string;
  created_at: string;
  source: "prepreg" | "raw";
  material_name: string;
  material_code: string;
  unit: string;
  movement_type: string;
  quantity: number;
  previous_stock: number | null;
  new_stock: number | null;
  who: string;
  reason: string;
  notes: string;
}

const actionMeta: Record<string, { label: string; cls: string; Icon: any }> = {
  in: { label: "Entrada", cls: "bg-green-500/10 text-green-600 border-green-500/20", Icon: ArrowUpCircle },
  out: { label: "Salida", cls: "bg-red-500/10 text-red-600 border-red-500/20", Icon: ArrowDownCircle },
  adjustment: { label: "Ajuste", cls: "bg-amber-500/10 text-amber-600 border-amber-500/20", Icon: RotateCcw },
};

export function MaterialTraceability() {
  const [sourceFilter, setSourceFilter] = useState("all");
  const [actionFilter, setActionFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["material-traceability"],
    queryFn: async (): Promise<TraceRow[]> => {
      const [inv, raw, prepMats, rawMats, profiles, users] = await Promise.all([
        api.from("inventory_movements").select("*").order("created_at", { ascending: false }).limit(2000),
        api.from("raw_material_movements").select("*").order("created_at", { ascending: false }).limit(2000),
        api.from("material_inventory").select("id,name,code,unit"),
        api.from("raw_materials").select("id,name,code,unit"),
        api.from("profiles").select("id,full_name"),
        api.from("users").select("id,email"),
      ]);
      const prepMap = new Map((prepMats.data || []).map((m: any) => [m.id, m]));
      const rawMap = new Map((rawMats.data || []).map((m: any) => [m.id, m]));
      const nameMap = new Map<string, string>();
      (profiles.data || []).forEach((p: any) => { if (p.full_name) nameMap.set(p.id, p.full_name); });
      (users.data || []).forEach((u: any) => { if (!nameMap.has(u.id) && u.email) nameMap.set(u.id, u.email); });
      const who = (id: string | null) => (id ? nameMap.get(id) || "Usuario" : "Sistema");

      const a: TraceRow[] = (inv.data || []).map((m: any) => {
        const mat = prepMap.get(m.material_inventory_id) || {};
        return {
          id: "iv_" + m.id, created_at: m.created_at, source: "prepreg",
          material_name: mat.name || "(material eliminado)", material_code: mat.code || "", unit: mat.unit || "",
          movement_type: m.movement_type, quantity: Number(m.quantity), previous_stock: m.previous_stock, new_stock: m.new_stock,
          who: who(m.performed_by), reason: m.reason || "", notes: m.notes || "",
        };
      });
      const b: TraceRow[] = (raw.data || []).map((m: any) => {
        const mat = rawMap.get(m.raw_material_id) || {};
        return {
          id: "rm_" + m.id, created_at: m.created_at, source: "raw",
          material_name: mat.name || "(material eliminado)", material_code: mat.code || "", unit: mat.unit || "",
          movement_type: m.movement_type, quantity: Number(m.quantity), previous_stock: m.previous_stock, new_stock: m.new_stock,
          who: who(m.performed_by), reason: m.reason || "", notes: m.notes || "",
        };
      });
      return [...a, ...b].sort((x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime());
    },
    refetchInterval: 30000,
  });

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (sourceFilter !== "all" && r.source !== sourceFilter) return false;
      if (actionFilter !== "all" && r.movement_type !== actionFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (!(`${r.material_name} ${r.material_code} ${r.who} ${r.reason}`.toLowerCase().includes(q))) return false;
      }
      if (dateFrom && new Date(r.created_at) < new Date(dateFrom)) return false;
      if (dateTo && new Date(r.created_at) > new Date(dateTo + "T23:59:59")) return false;
      return true;
    });
  }, [rows, sourceFilter, actionFilter, search, dateFrom, dateTo]);

  const stats = useMemo(() => ({
    total: filtered.length,
    in: filtered.filter((r) => r.movement_type === "in").length,
    out: filtered.filter((r) => r.movement_type === "out").length,
    adj: filtered.filter((r) => r.movement_type === "adjustment").length,
  }), [filtered]);

  const exportCSV = () => {
    const head = ["Fecha", "Inventario", "Material", "Codigo", "Accion", "Cantidad", "Unidad", "Stock anterior", "Stock nuevo", "Responsable", "Motivo", "Notas"];
    const lines = filtered.map((r) => [
      format(new Date(r.created_at), "yyyy-MM-dd HH:mm"),
      r.source === "prepreg" ? "Preimpregnado" : "Materia prima",
      r.material_name, r.material_code,
      actionMeta[r.movement_type]?.label || r.movement_type,
      r.quantity, r.unit, r.previous_stock ?? "", r.new_stock ?? "", r.who, r.reason, r.notes,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const csv = "﻿" + [head.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `trazabilidad_materiales_${format(new Date(), "yyyyMMdd_HHmm")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <History className="w-5 h-5" /> Trazabilidad de Materiales
          </h2>
          <p className="text-sm text-muted-foreground">
            Registro de entradas, salidas y ajustes — quién, cuándo y por qué. Para consulta y auditoría.
          </p>
        </div>
        <Button variant="outline" onClick={exportCSV} className="gap-2" disabled={filtered.length === 0}>
          <Download className="w-4 h-4" /> Exportar CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-muted-foreground">Movimientos</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-2xl font-bold text-green-600">{stats.in}</p><p className="text-xs text-muted-foreground">Entradas</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-2xl font-bold text-red-600">{stats.out}</p><p className="text-xs text-muted-foreground">Salidas</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-2xl font-bold text-amber-600">{stats.adj}</p><p className="text-xs text-muted-foreground">Ajustes</p></CardContent></Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Buscar material, responsable, motivo..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo el inventario</SelectItem>
              <SelectItem value="prepreg">Preimpregnados</SelectItem>
              <SelectItem value="raw">Materias primas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[150px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las acciones</SelectItem>
              <SelectItem value="in">Entradas</SelectItem>
              <SelectItem value="out">Salidas</SelectItem>
              <SelectItem value="adjustment">Ajustes</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px]" />
            <span className="text-muted-foreground text-sm">a</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px]" />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Movimientos ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-10 text-muted-foreground">Cargando...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">Sin movimientos para los filtros seleccionados.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Inventario</TableHead>
                    <TableHead>Material</TableHead>
                    <TableHead>Acción</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                    <TableHead>Responsable</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.slice(0, 500).map((r) => {
                    const am = actionMeta[r.movement_type] || { label: r.movement_type, cls: "", Icon: RotateCcw };
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-sm">{format(new Date(r.created_at), "d MMM yyyy HH:mm", { locale: es })}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">{r.source === "prepreg" ? "Preimpregnado" : "Materia prima"}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium text-sm">{r.material_name}</div>
                          {r.material_code && <div className="text-xs text-muted-foreground">{r.material_code}</div>}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={am.cls + " gap-1"}>
                            <am.Icon className="w-3 h-3" /> {am.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium">{r.quantity} {r.unit}</TableCell>
                        <TableCell className="text-right text-xs text-muted-foreground whitespace-nowrap">
                          {r.previous_stock != null && r.new_stock != null ? `${r.previous_stock} -> ${r.new_stock}` : "-"}
                        </TableCell>
                        <TableCell className="text-sm">{r.who}</TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[220px] truncate" title={[r.reason, r.notes].filter(Boolean).join(" — ")}>
                          {[r.reason, r.notes].filter(Boolean).join(" — ") || "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filtered.length > 500 && (
                <p className="text-xs text-muted-foreground mt-2 text-center">Mostrando los 500 más recientes. Usa los filtros o exporta a CSV para ver todo.</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
